import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, ShieldCheck, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function CustomerAuthModal() {
  const { loginOpen, setLoginOpen, setCustomer, cart, setCheckoutOpen } = useStore();
  const [step, setStep] = useState("details");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setLoginOpen(false);
    setStep("details");
    setOtp("");
    setDemoOtp(null);
  };

  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3.5 text-sm outline-none focus:border-terracotta transition-colors";

  const sendOtp = async () => {
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter your name and 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/customer/send-otp", form);
      setStep("otp");
      setOtp("");
      if (data.demo) {
        setDemoOtp(data.dev_otp);
      } else {
        toast.success(`OTP sent to +91 ${form.phone}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (otp.trim().length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/customer/verify-otp", { phone: form.phone, otp: otp.trim() });
      localStorage.setItem("lj_customer_token", data.token);
      setCustomer({ phone: data.phone, name: data.name, email: data.email });
      toast.success(`Welcome, ${data.name}! You're logged in.`);
      close();
      if (cart.length > 0) setCheckoutOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {loginOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
          onClick={close}
          data-testid="customer-login-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-cream w-full max-w-sm rounded-t-[2rem] sm:rounded-[2rem] p-6 sm:p-8"
          >
            <div className="flex items-center justify-between mb-1">
              <img src="/logo.png" alt="Baeboo's Wardrobe" className="h-12 w-12 object-contain" />
              <button onClick={close} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="customer-login-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="font-serif font-black text-2xl text-ink mt-2">
              {step === "details" ? "Login with OTP" : "Verify your number"}
            </h3>
            <p className="text-sm text-charcoal mt-1 mb-6">
              {step === "details"
                ? "Track orders, faster checkout — one quick OTP."
                : `We sent a 6-digit code to +91 ${form.phone}`}
            </p>

            {step === "details" ? (
              <div className="space-y-3">
                <input className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="login-name-input" />
                <input className={inputCls} type="email" placeholder="Email (for order updates)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
                  <input className={`${inputCls} pl-11`} placeholder="Mobile number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="login-phone-input" />
                </div>
                <button
                  onClick={sendOtp}
                  disabled={busy}
                  className="w-full rounded-full bg-terracotta text-white py-4 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="login-send-otp-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {busy ? "Sending OTP..." : "Send OTP"}
                </button>
                <p className="text-center font-mono text-[10px] text-charcoal/60">Use the same mobile number you order with — your orders link automatically.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <input
                  className={`${inputCls} text-center font-mono text-2xl tracking-[0.5em] font-bold`}
                  placeholder="••••••"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  data-testid="login-otp-input"
                />
                {demoOtp && (
                  <div className="bg-peach rounded-2xl p-3.5 text-center" data-testid="login-demo-otp">
                    <p className="font-mono text-[10px] tracking-widest uppercase text-terracotta-dark">Demo mode — SMS not connected yet</p>
                    <p className="font-mono font-bold text-2xl text-ink mt-1">{demoOtp}</p>
                  </div>
                )}
                <button
                  onClick={verify}
                  disabled={busy}
                  className="w-full rounded-full bg-ink text-cream py-4 text-sm font-semibold hover:bg-terracotta transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="login-verify-otp-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {busy ? "Verifying..." : "Verify & Login"}
                </button>
                <button onClick={() => { setStep("details"); setDemoOtp(null); }} className="w-full text-center font-mono text-[10px] tracking-widest uppercase text-charcoal/60 hover:text-terracotta" data-testid="login-change-number">
                  ← Change number
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
